import AnamnesisView, { Views as ANAMNESIS_VIEWS } from 'components/organisms/Anamnesis';
import { routes } from 'defs';
import i18n from 'helper/i18n';
import { template } from 'helper/string';
import { getWeightForDisplay } from 'helper/weight';
import camelCase from 'lodash.camelcase';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, Dispatch } from 'redux';
import {
	actionCreators,
	getAllergies,
	getBirthdate,
	getGender,
	getGenderMeta,
	getHealthTarget,
	getHeight,
	getIntensity,
	getNutritionPhilosophy,
	getWeight,
	getWeightReduceGoal,
} from 'store/profile';
import { IRootState } from 'store/rootReducer';
import { WithRouterType } from 'types/withRouter';
import {
	allergyList,
	mapAllergiesToIcon,
	mapNutritionPhilosophyToIcon,
	nutritionPhilosophyList,
} from '../../../../defs/dietaryHabits';
import { mapParentStateToProps } from '../Anamnesis';

const defaultValue: string = i18n(`react.impulsCoach.components.anamnesis.verify.defaultValue`);
const defaultValueDietaryHabits: string = i18n(
	`react.impulsCoach.components.anamnesis.verify.dietaryHabits.defaultValue`
);
const durationIndexMap = ['low', 'medium', 'high'];

const mapStateToProps = (state: IRootState, props: WithRouterType) => ({
	...mapParentStateToProps(state, props),
	currentStep: ANAMNESIS_VIEWS.VERIFY,
	introTitle: i18n(`react.impulsCoach.components.anamnesis.verify.introTitle`),
	introText: i18n(`react.impulsCoach.components.anamnesis.verify.introText`),
	// because https://inside.namics.com/display/TAFrontend/questions/520323125/answers/520716431
	title: undefined,
	personalSummary: {
		title: i18n(`react.impulsCoach.components.anamnesis.verify.personal.title`),
		elements: [
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.personal.genderCaption`),
				value: ((gender: undefined | string | null, meta: any) => {
					if (gender) {
						const baseGenderName = gender === 'M' ? 'male' : 'female';
						const isPregnant = !!meta.pregnant;
						const genderName = isPregnant ? 'femaleAndPregnant' : baseGenderName;
						return i18n(`react.impulsCoach.components.anamnesis.verify.personal.gender.${genderName}`);
					}

					return defaultValue;
				})(getGender(state), getGenderMeta(state)),
				icon: 'gender',
				_target: routes.anamnesis.selectGender,
			},
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.personal.birthdateCaption`),
				value: ((birthdate: { year: number; monthValue: number; dayOfMonth: number } | null): string => {
					if (typeof birthdate !== 'object' || birthdate === null) {
						return defaultValue;
					}

					if (birthdate.year === null || birthdate.monthValue === null || birthdate.dayOfMonth === null) {
						return defaultValue;
					}

					const mask: string = 'DD.MM.YYYY';

					return moment([birthdate.year, birthdate.monthValue - 1, birthdate.dayOfMonth]).format(mask);
				})(getBirthdate(state)),
				icon: 'cupcake',
				_target: routes.anamnesis.determineAge,
			},
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.personal.sizeCaption`),
				value: ((height) => {
					if (height) {
						return `${height} ${i18n(`react.impulsCoach.components.anamnesis.verify.personal.sizeUnit`)}`;
					}

					return defaultValue;
				})(getHeight(state)),
				icon: 'measuring-tape',
				_target: routes.anamnesis.determineHeight,
			},
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.personal.weightCaption`),
				value: ((weight) => {
					if (weight) {
						return `${weight} ${i18n(`react.impulsCoach.components.anamnesis.verify.personal.weightUnit`)}`;
					}

					return defaultValue;
				})(getWeight(state)),
				icon: 'scale',
				_target: routes.anamnesis.determineWeight,
			},
		],
	},
	targetSummary: {
		title: i18n(`react.impulsCoach.components.anamnesis.verify.target.title`),
		elements: [
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.target.healthTargetCaption`),
				value: ((target: string | null, reducer: number | null): string => {
					if (target) {
						target = camelCase(target.toString());
						const textTemplate = i18n(
							`react.impulsCoach.components.anamnesis.verify.healthTarget.${target}`
						);

						// reducer !== 0 means the health goal is reduce weight
						if (state.profile && state.profile.userData && reducer) {
							const userWeight = parseFloat(state.profile.userData.weight.toString());
							const reduceWeight = parseFloat(reducer.toString());

							return template(textTemplate, {
								weightTarget: getWeightForDisplay(userWeight - reduceWeight),
							});
						}

						return textTemplate;
					}

					return defaultValue;
				})(getHealthTarget(state), getWeightReduceGoal(state)),
				icon: 'wishlist',
				_target: routes.anamnesis.defineHealthTarget,
			},
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.target.intensityCaption`),
				value: ((intensity: number | null): string => {
					if (intensity && !isNaN(intensity)) {
						const intensityModifier = durationIndexMap[intensity - 1];
						const intensityLabelValue = intensityModifier
							? i18n(`react.impulsCoach.components.Level.${intensityModifier}.title`)
							: null;

						if (intensityLabelValue) {
							return `${intensityLabelValue}`;
						}
					}

					return defaultValue;
				})(getIntensity(state)),
				icon: 'clock',
				_target: routes.anamnesis.selectActivityLevel,
			},
		],
	},
	dietaryHabitsSummary: {
		title: i18n(`react.impulsCoach.components.anamnesis.verify.dietaryHabits.title`),
		elements: [
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.dietaryHabits.nutritionPhilosophyCaption`),
				value: ((nutrition) => {
					if (nutrition) {
						const nutritionItem = nutritionPhilosophyList.find((el) => el.id === nutrition);
						return nutritionItem ? `${nutritionItem.text}` : defaultValueDietaryHabits;
					}

					return defaultValueDietaryHabits;
				})(getNutritionPhilosophy(state)),
				icon: ((nutrition) => {
					if (nutrition) {
						return mapNutritionPhilosophyToIcon(nutrition);
					}

					return defaultValueDietaryHabits;
				})(getNutritionPhilosophy(state)),
				_target: routes.anamnesis.selectDietaryHabits,
			},
			{
				label: i18n(`react.impulsCoach.components.anamnesis.verify.dietaryHabits.allergiesCaption`),
				value: ((allergies) => {
					if (allergies) {
						const allergiesItems = allergyList.filter((el) => allergies.includes(el.id));
						return allergiesItems.length !== 0
							? `${allergiesItems.map((i) => i.text).join(', ')}`
							: defaultValueDietaryHabits;
					}

					return defaultValueDietaryHabits;
				})(getAllergies(state)),
				icon: ((allergies) => {
					if (allergies) {
						return mapAllergiesToIcon(allergies);
					}

					return defaultValueDietaryHabits;
				})(getAllergies(state)),
				_target: routes.anamnesis.selectDietaryHabits,
			},
		],
	},
	buttons: [
		{
			link: {
				modifier: 'button',
				title: i18n(`react.impulsCoach.components.anamnesis.verify.submitButtonText`),
				openInNewWindow: false,
				iconRight: 'link-arrow',
			},
		},
	],
});

const mapDispatchToProps = (dispatch: Dispatch) => {
	return bindActionCreators(actionCreators, dispatch);
};

export type VerifyContainerProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AnamnesisView) as any);
